import React from "react";
import { AntiMetalButton } from "./anti-metal-button";
import { cn } from "../../lib/utils";

export function Hero({
  eyebrow = "WHAT WE DO",
  title = "The capabilities behind better growth.",
  subtitle,
  description,
  ctaLabel = "Explore our capabilities ↓",
  ctaHref = "#divisions",
  className,
}) {
  return (
    <section
      id="hero"
      className={cn("hero1-section", className)}
    >
      <style>{`
        @keyframes hero-fade-in {
          0% {
            opacity: 0;
            transform: translateY(24px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes hero-fade-up {
          0% {
            opacity: 0;
            transform: translate(-50%, 50px);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        .hero1-section {
          position: relative;
          width: 100%;
          min-height: calc(100vh - 40px);
          padding-top: clamp(140px, 18vh, 200px);
          padding-bottom: clamp(140px, 20vh, 240px);
          padding-left: clamp(20px, 5vw, 48px);
          padding-right: clamp(20px, 5vw, 48px);
          text-align: center;
          overflow: hidden;
          background: linear-gradient(to bottom, #000000 0%, #060609 35%, #0b0b10 70%, #060608 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          box-sizing: border-box;
        }

        /* Top White Grid with Smooth Radial Fade */
        .hero1-grid {
          position: absolute;
          inset: 0;
          height: 100%;
          width: 100%;
          background-image: 
            linear-gradient(to right, rgba(255, 255, 255, 0.16) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.16) 1px, transparent 1px);
          background-size: 5.5rem 4.5rem;
          -webkit-mask-image: radial-gradient(ellipse 95% 75% at 50% 0%, #000000 30%, rgba(0, 0, 0, 0.45) 60%, transparent 95%);
          mask-image: radial-gradient(ellipse 95% 75% at 50% 0%, #000000 30%, rgba(0, 0, 0, 0.45) 60%, transparent 95%);
          pointer-events: none;
          z-index: 0;
        }

        /* Giant Radial Horizon Accent (Curved Planet Horizon Disc) */
        .hero1-radial-accent {
          position: absolute;
          left: 50%;
          top: calc(100% - 110px);
          height: 520px;
          width: 760px;
          transform: translateX(-50%);
          border-radius: 100%;
          border: 1.5px solid rgba(255, 255, 255, 0.28);
          background: #000000;
          background-image: radial-gradient(closest-side, #060608 76%, rgba(236, 43, 40, 0.45) 93%, #ffffff 100%);
          box-shadow: 0 -24px 90px rgba(236, 43, 40, 0.4), inset 0 2px 14px rgba(255, 255, 255, 0.7);
          pointer-events: none;
          z-index: 1;
          animation: hero-fade-up 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @media (min-width: 768px) {
          .hero1-radial-accent {
            width: 1150px;
            height: 620px;
            top: calc(100% - 140px);
          }
        }

        @media (min-width: 1024px) {
          .hero1-radial-accent {
            width: 145%;
            max-width: 1900px;
            height: 780px;
            top: calc(100% - 170px);
          }
        }

        /* Ambient glow over horizon */
        .hero1-horizon-glow {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 95%;
          max-width: 1300px;
          height: 240px;
          background: radial-gradient(ellipse at 50% 100%, rgba(236, 43, 40, 0.28) 0%, rgba(255, 255, 255, 0.09) 35%, transparent 70%);
          filter: blur(45px);
          pointer-events: none;
          z-index: 2;
        }

        /* Smooth Bottom Fade Overlay */
        .hero1-bottom-fade {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 200px;
          background: linear-gradient(to bottom, transparent 0%, rgba(6, 6, 8, 0.25) 30%, rgba(6, 6, 8, 0.75) 70%, #060608 100%);
          pointer-events: none;
          z-index: 3;
        }

        .hero1-content {
          position: relative;
          z-index: 4;
          max-width: 980px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: hero-fade-in 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* Eyebrow badge */
        .hero1-eyebrow-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 22px;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(236, 43, 40, 0.08) 100%);
          border: 1.5px solid rgba(255, 255, 255, 0.16);
          color: #ffffff;
          font-family: 'Montserrat', sans-serif;
          font-size: 0.74rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          margin-bottom: 28px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
          transition: transform 0.25s ease, border-color 0.25s ease;
          text-decoration: none;
        }

        .hero1-eyebrow-pill:hover {
          transform: translateY(-1px);
          border-color: rgba(236, 43, 40, 0.5);
        }

        .hero1-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ec2b28;
          box-shadow: 0 0 8px #ec2b28;
        }

        /* Headline */
        .hero1-headline {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(2.5rem, 6.4vw, 5.5rem);
          font-weight: 900;
          letter-spacing: -0.04em;
          line-height: 1.06;
          color: #ffffff;
          margin: 0 0 24px 0;
          text-wrap: balance;
          background: linear-gradient(180deg, #ffffff 40%, rgba(255, 255, 255, 0.6) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Subtitle */
        .hero1-subtitle {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(1.05rem, 1.6vw, 1.28rem);
          font-weight: 500;
          line-height: 1.65;
          color: #e5e7eb;
          max-width: 840px;
          margin: 0 auto 16px;
          text-wrap: balance;
        }

        /* Secondary Description */
        .hero1-desc {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(0.88rem, 1.15vw, 1rem);
          font-weight: 400;
          line-height: 1.75;
          color: #9ca3af;
          max-width: 780px;
          margin: 0 auto 38px;
        }

        /* CTA Area */
        .hero1-cta-wrap {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 4px;
        }

        @media (max-width: 768px) {
          .hero1-section {
            padding-top: 120px;
            padding-bottom: 120px;
            min-height: auto;
          }
          .hero1-headline {
            font-size: clamp(2.1rem, 9vw, 3.2rem);
            margin-bottom: 18px;
          }
          .hero1-subtitle {
            font-size: 0.98rem;
            line-height: 1.6;
          }
          .hero1-desc {
            font-size: 0.84rem;
            margin-bottom: 28px;
          }
        }
      `}</style>

      {/* Top White Grid Background with smooth fade */}
      <div className="hero1-grid" />

      {/* Giant Radial Horizon Accent at Bottom */}
      <div className="hero1-radial-accent" />

      {/* Horizon Glow */}
      <div className="hero1-horizon-glow" />

      {/* Smooth Bottom Fade Overlay */}
      <div className="hero1-bottom-fade" />

      <div className="hero1-content">
        {/* Eyebrow */}
        {eyebrow && (
          <div className="hero1-eyebrow-pill">
            <span className="hero1-eyebrow-dot" />
            <span>{eyebrow}</span>
          </div>
        )}

        {/* Title */}
        <h1 className="hero1-headline">
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <p className="hero1-subtitle">
            {subtitle}
          </p>
        )}

        {/* Description */}
        {description && (
          <p className="hero1-desc">
            {description}
          </p>
        )}

        {/* CTA */}
        {ctaLabel && (
          <div className="hero1-cta-wrap">
            <AntiMetalButton as="a" href={ctaHref}>
              {ctaLabel}
            </AntiMetalButton>
          </div>
        )}
      </div>
    </section>
  );
}

export default Hero;
